import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from "uuid";

import { ExpandLess, ExpandMore } from '@mui/icons-material';
import Grid from '@mui/material/Grid2';

import {
  ModelBase,
} from "@models";
import {
  LootEntry,
  LootItemGroup,
  LootTable,
} from "@dndModels";
import { allNamedItems, loadEditorItems } from "@dndUtilities/encounter_loot";

import API from "@utilities/smart_api";
import CommonUtilities from "@utilities/common_utilities";

import ModelBaseInput from "@dndComponents/ModelBaseInput";
import LootEntryInput from "@dndComponents/other/LootEntryInput";
import LootTableImport from "@dndComponents/other/LootTableImport";
import StringBox from "@components/input/strings/StringBox";
import CopyButton from "@components/input/buttons/icon_buttons/CopyButton";
import DeleteButton from "@components/input/buttons/icon_buttons/DeleteButton";
import DandDButtonBox from '@dndComponents/DandDButtonBoxSimple';

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

const entries_per_page = 20;

interface Props {
  obj?: ModelBase;
}

const crRange = (entry: LootEntry) => {
  if (entry.min_cr < 0 && entry.max_cr < 0) {
    return "";
  } else if (entry.min_cr === entry.max_cr) {
    return `CR ${entry.min_cr}`;
  }
  return `CR ${entry.min_cr < 0 ? "0" : entry.min_cr} - ${entry.max_cr < 0 ? "Any" : entry.max_cr}`;
}

const diceString = (count: number, size: number) => size === 1 ? `${count}` : `${count}d${size}`;

const groupSummary = (group: LootItemGroup, item_names: Map<string, string>, table_names: Map<string, string>) => {
  const dice = diceString(group.count.count, group.count.size);
  switch (group.source) {
    case "Specific Item": return `${dice} x ${item_names.get(group.item_id) ?? "(item not set)"}`;
    case "Loot Table": return `${dice} roll(s) on ${table_names.get(group.loot_table_id) ?? "(table not set)"}`;
    default: {
      const value = group.min_value < 0 && group.max_value < 0 ? "" :
        ` worth ${group.min_value < 0 ? 0 : group.min_value}-${group.max_value < 0 ? "any" : group.max_value} gp`;
      return `${dice} item(s)${value}`;
    }
  }
}

const entrySummary = (entry: LootEntry, item_names: Map<string, string>, table_names: Map<string, string>) => {
  const pieces: string[] = [entry.guaranteed ? "Guaranteed" : `Weight ${entry.weight}`];
  if (entry.difficulties.length > 0) {
    pieces.push(entry.difficulties.join("/"));
  }
  const cr = crRange(entry);
  if (cr !== "") {
    pieces.push(cr);
  }
  if (entry.environment_ids.length > 0) {
    pieces.push(`${entry.environment_ids.length} Environment(s)`);
  }
  if (!entry.creature_limit.isEmpty()) {
    pieces.push(`Creatures: ${entry.creature_limit.toString()}`);
  }
  pieces.push(...entry.item_groups.map(o => groupSummary(o, item_names, table_names)));
  if (!entry.money.isEmpty()) {
    pieces.push("Money");
  }
  return pieces.join(", ");
}

export default function LootTableEdit({ obj: param_obj }: Props) {
  const [obj, setObj] = useState<LootTable>(param_obj instanceof LootTable ? param_obj : api.applyWorkingOn(new LootTable()));
  const [change_time, setChangeTime] = useState(0);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [item_options, setItemOptions] = useState<{ id: string, name: string }[] | null>(null);
  const [table_names, setTableNames] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    loadEditorItems().then(() => {
      setItemOptions(allNamedItems().sort((a, b) => a.name.localeCompare(b.name)));
    });
    api.getObjects({ data_type: "loot_table" }).then(res => {
      setTableNames(new Map(res.map(o => [o._id, o.name])));
    });
  }, []);

  const item_names = new Map((item_options ?? []).map(o => [o.id, o.name]));

  const disabled = api.user === null || !api.online || (!api.user.admin && obj._id !== "" && obj.owner_id !== api.user._id);

  const updateObj = (updater: (o: LootTable) => void) => {
    const new_obj = obj.clone(); // clone, don't mutate
    updater(new_obj);
    setObj(new_obj);
    setChangeTime(common_util.get_time_number());
  };

  const updateEntry = (true_id: string, update: (entry: LootEntry) => void) => {
    updateObj(o => {
      const entry = o.entries.find(e => e.true_id === true_id);
      if (entry) {
        update(entry);
      }
    });
  };

  const toggleExpanded = (true_id: string) => {
    setExpanded(expanded.includes(true_id) ? expanded.filter(o => o !== true_id) : [...expanded, true_id]);
  };

  const shown = search === "" ? obj.entries :
    obj.entries.filter(o => o.name.toLowerCase().includes(search.toLowerCase()));
  const page_count = Math.max(Math.ceil(shown.length / entries_per_page), 1);
  const use_page = Math.min(page, page_count - 1);
  const current_page = shown.slice(use_page * entries_per_page, (use_page + 1) * entries_per_page);

  return (
    <ModelBaseInput
      obj={obj}
      disabled={disabled}
      change_time={change_time}
      onChange={(changed: ModelBase) => {
        if (changed instanceof LootTable) {
          const new_obj = obj;
          new_obj.copy(changed);
          setObj(new_obj);
          setChangeTime(common_util.get_time_number());
        }
      }}>
      <Grid container spacing={1} direction="row">
        <Grid size={12} className="header">
          Entries ({ obj.entries.length })
        </Grid>
        <Grid size={12} className="small_label gray">
          Guaranteed entries that fit the encounter are each given once,
          then each roll picks one of the other entries that fit, by weight, from all of the campaign&apos;s loot tables.
        </Grid>
        <Grid size={6}>
          <StringBox
            name="Search Entries"
            value={search}
            onBlur={(changed: string) => {
              setSearch(changed);
              setPage(0);
            }}
          />
        </Grid>
        <Grid size={3}>
          <LootTableImport
            item_options={item_options}
            disabled={disabled}
            onImport={(entries: LootEntry[]) => {
              updateObj(o => { o.entries.push(...entries); });
              setSearch("");
              setPage(Math.floor(obj.entries.length / entries_per_page));
            }}
          />
        </Grid>
        <Grid size={3}>
          <DandDButtonBox
            name="Add Entry"
            disabled={disabled}
            onClick={() => {
              const entry = new LootEntry();
              entry.name = `Entry ${obj.entries.length + 1}`;
              updateObj(o => { o.entries.push(entry); });
              setSearch("");
              setPage(Math.floor(obj.entries.length / entries_per_page));
              setExpanded([...expanded, entry.true_id]);
            }}
          />
        </Grid>
        { current_page.map(entry => {
          const is_expanded = expanded.includes(entry.true_id);
          return (
            <Grid key={entry.true_id} size={12} className="padded bordered">
              <Grid container spacing={0} direction="row">
                <Grid size={10} className="clickable" onClick={() => toggleExpanded(entry.true_id)}>
                  <div className="header">{ entry.name === "" ? "(Unnamed)" : entry.name }</div>
                  <div className="small_label gray">{ entrySummary(entry, item_names, table_names) }</div>
                </Grid>
                <Grid size={2} className="centered">
                  <CopyButton
                    disabled={disabled}
                    onClick={() => {
                      const copy = entry.clone();
                      copy.true_id = uuidv4().toString();
                      copy.name = `${entry.name} (Copy)`;
                      updateObj(o => {
                        const index = o.entries.findIndex(e => e.true_id === entry.true_id);
                        o.entries.splice(index + 1, 0, copy);
                      });
                    }}
                  />
                  <DeleteButton
                    disabled={disabled}
                    no_fab
                    onClick={() => updateObj(o => { o.entries = o.entries.filter(e => e.true_id !== entry.true_id); })}
                  />
                  <span className="clickable" onClick={() => toggleExpanded(entry.true_id)}>
                    { is_expanded ? <ExpandLess /> : <ExpandMore /> }
                  </span>
                </Grid>
                { is_expanded &&
                  <Grid size={12} className="padded">
                    <LootEntryInput
                      entry={entry}
                      disabled={disabled}
                      item_options={item_options ?? []}
                      onChange={update => updateEntry(entry.true_id, update)}
                    />
                  </Grid>
                }
              </Grid>
            </Grid>
          );
        })}
        { page_count > 1 &&
          <Grid size={12}>
            <Grid container spacing={1}>
              <Grid size={2}>
                <DandDButtonBox name="Previous" disabled={use_page === 0} onClick={() => setPage(use_page - 1)} />
              </Grid>
              <Grid size={8} className="centered">
                Page { use_page + 1 } of { page_count }
              </Grid>
              <Grid size={2}>
                <DandDButtonBox name="Next" disabled={use_page + 1 >= page_count} onClick={() => setPage(use_page + 1)} />
              </Grid>
            </Grid>
          </Grid>
        }
      </Grid>
    </ModelBaseInput>
  );
}
